import { PageShell } from "@superadmin/components/PageShell";
import { RateFormDialog } from "@superadmin/components/RateForm";
import {
	CSV_SERVICES,
	callsToCsv,
	describePricing,
	monthToDate,
	parseSellingPrice,
	postCallMicros,
	scopeLabel,
	splitList,
} from "@superadmin/utils/cost";
import { formatClock, formatCount, formatEpoch, formatMicros } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Tooltip } from "@voicely/ui-mui/data-display/Tooltip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { AlertTitle } from "@voicely/ui-mui/feedback/AlertTitle";
import { Button } from "@voicely/ui-mui/form/Button";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Box } from "@voicely/ui-mui/layout/Box";
import { Card } from "@voicely/ui-mui/layout/Card";
import { Grid } from "@voicely/ui-mui/layout/Grid";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { Pagination } from "@voicely/ui-mui/navigation/Pagination";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import {
	CALL_OUTCOMES,
	type CallCost,
	COST_GROUP_BYS,
	COST_PURPOSES,
	COST_SERVICES,
	COSTED_CALL_DIRECTIONS,
	type CostCalculatorInput,
	type RateInput,
} from "@voicely/zod-schemas/costing.zod";
import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { z } from "zod";

const text = z.string();
const flagSchema = z.enum(["0", "1"]);
const dateSchema = z.iso.date();
const pageSchema = z.coerce.number().int().min(1).max(10_000);
const PAGE_SIZE = 100;

const SERVICE_LABELS: Record<string, string> = {
	telephony: "Telephony",
	stt: "Speech-to-text",
	llm: "Inference",
	tts: "Synthesis",
	storage: "Recording storage",
};

/** Where a call's connected minutes came from — the carrier's word, or ours standing in for it. */
const CONNECTED_SOURCE_LABEL: Record<CallCost["connectedSource"], string> = {
	carrier: "Carrier talk time, as the carrier reported it",
	clocks: "The carrier reported no duration, so its answer→hangup timestamps",
	session: "Browser call: the session's own clock",
	none: "Never connected",
};

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
	<Card variant="outlined" sx={{ p: 2, height: "100%" }}>
		<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
			{label}
		</Typography>
		<Typography variant="h5" sx={{ mt: 0.5 }}>
			{value}
		</Typography>
		{hint && (
			<Typography variant="caption" color="text.secondary">
				{hint}
			</Typography>
		)}
	</Card>
);

/** Talk time as a clock — the pulse rounds seconds, so seconds are what to show. */
const minutes = formatClock;

/** Basis points → "12.5%". */
const percent = (bps: number | null) => (bps === null ? "—" : `${(bps / 100).toFixed(1)}%`);

function download(name: string, body: string) {
	const url = URL.createObjectURL(new Blob([body], { type: "text/csv;charset=utf-8" }));
	const link = document.createElement("a");

	link.href = url;
	link.download = name;
	link.click();
	URL.revokeObjectURL(url);
}

/**
 * The cost calculator: per-call cost, the cumulative total and the average
 * cost per connected minute, for any set of filters, at the rates valid on
 * each call's date.
 *
 * ## Every filter is in the URL
 *
 * A cost question is one somebody sends to somebody else — "look at what this
 * batch cost last month" is a link, not a list of dropdowns to set. Dates,
 * workspaces, agents, batch, direction, outcome, the service/vendor/model
 * narrowing, the grouping, the `as of` replay and the page are all query
 * params, and a rubbish value in a pasted link degrades to the default.
 *
 * What-if rates are the exception, deliberately: they are unsaved form input,
 * and a half-typed rate does not belong in browser history.
 *
 * ## Post-call analysis is a cut through Inference, not a sixth service
 *
 * Summary and extraction are `llm` lines, so their money is already inside
 * "Inference". They get their own card and column anyway because they are the
 * one cost here that follows transcript length rather than minutes — and the
 * card says it is included, so nobody adds it to the service row again.
 *
 * ## The gaps are the point, not decoration
 *
 * A line no rate prices is a missing number, never a zero, so every total here
 * says when it is a floor: unpriced bindings are named with a link to the page
 * that fixes them, an unconverted currency is called out apart from them, and
 * a window past the call cap refuses to present itself as a total.
 */
export const CostingPage = () => {
	const navigate = useNavigate();
	const defaults = useMemo(() => monthToDate(), []);

	const [from, setFrom] = useUrlState("from", dateSchema, defaults.from);
	const [to, setTo] = useUrlState("to", dateSchema, defaults.to);
	const [teams, setTeams] = useUrlState("teams", text, "");
	const [agents, setAgents] = useUrlState("agents", text, "");
	const [batch, setBatch] = useUrlState("batch", text, "");
	const [direction, setDirection] = useUrlState("direction", text, "");
	const [outcome, setOutcome] = useUrlState("outcome", text, "all");
	const [service, setService] = useUrlState("service", text, "");
	const [vendor, setVendor] = useUrlState("vendor", text, "");
	const [model, setModel] = useUrlState("model", text, "");
	const [purpose, setPurpose] = useUrlState("purpose", text, "");
	const [groupBy, setGroupBy] = useUrlState("group", text, "none");
	const [asOf, setAsOf] = useUrlState("asof", text, "");
	const [page, setPage] = useUrlState("page", pageSchema, 1);
	const [openCall, setOpenCall] = useUrlState("call", text, "");
	// The customer's price, typed per calculation and never saved: in the URL
	// so a margin someone is shown is a link they can re-open, like every
	// other input here.
	const [sellPrice, setSellPrice] = useUrlState("sell", text, "");
	const [sellPulse, setSellPulse] = useUrlState("pulse", text, "");
	const [sellFirst, setSellFirst] = useUrlState("first", text, "");
	const selling = parseSellingPrice(sellPrice, sellPulse, sellFirst);

	// Unsaved input, so component state; the dialog that edits it is URL state.
	const [drafts, setDrafts] = useState<RateInput[]>([]);
	const [whatIf, setWhatIf] = useUrlState("whatif", flagSchema, "0");
	const draftOpen = whatIf === "1";
	const setDraftOpen = (open: boolean) => setWhatIf(open ? "1" : "0");

	const teamIds = splitList(teams);
	const agentIds = splitList(agents);

	const input: CostCalculatorInput = {
		filters: {
			from,
			to,
			teamIds,
			agentIds,
			batchId: batch || undefined,
			direction: COSTED_CALL_DIRECTIONS.find((d) => d === direction),
			outcome: CALL_OUTCOMES.find((o) => o === outcome) ?? "all",
			service: COST_SERVICES.find((s) => s === service),
			vendor: vendor || undefined,
			model: model || undefined,
			purpose: COST_PURPOSES.find((p) => p === purpose),
		},
		selling,
		groupBy: COST_GROUP_BYS.find((g) => g === groupBy) ?? "none",
		asOf: asOf ? Date.parse(asOf) || undefined : undefined,
		draftRates: drafts,
		callsLimit: PAGE_SIZE,
		callsOffset: (page - 1) * PAGE_SIZE,
	};

	const result = useQuery(
		orpc.superAdmin.costCalculator.queryOptions({ input, placeholderData: keepPreviousData }),
	);
	const teamsQuery = useQuery(orpc.superAdmin.listTeams.queryOptions());
	const agentsQuery = useQuery(orpc.superAdmin.listAgents.queryOptions());
	const batchesQuery = useQuery(orpc.superAdmin.listBatches.queryOptions());

	// Any filter change goes back to page one; a page number from another
	// filter set points at rows that are not there.
	const refilter =
		<T,>(setter: (value: T) => void) =>
		(value: T) => {
			setter(value);
			setPage(1);
		};

	const toolbar = (
		<Stack spacing={1.5}>
			<Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
				<TextField
					size="small"
					type="date"
					label="From (IST)"
					value={from}
					onChange={(e) => refilter(setFrom)(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
				<TextField
					size="small"
					type="date"
					label="To (IST, inclusive)"
					value={to}
					onChange={(e) => refilter(setTo)(e.target.value)}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
				<TextField
					select
					size="small"
					label="Workspaces"
					value={teamIds}
					onChange={(e) => refilter(setTeams)([e.target.value].flat().join(","))}
					slotProps={{ select: { multiple: true } }}
					sx={{ minWidth: 200 }}
				>
					{(teamsQuery.data ?? []).map((team) => (
						<MenuItem key={team.id} value={team.id}>
							{team.name}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Voice agents"
					value={agentIds}
					onChange={(e) => refilter(setAgents)([e.target.value].flat().join(","))}
					slotProps={{ select: { multiple: true } }}
					sx={{ minWidth: 200 }}
				>
					{(agentsQuery.data ?? [])
						.filter(
							(agent) => teamIds.length === 0 || (agent.teamId && teamIds.includes(agent.teamId)),
						)
						.map((agent) => (
							<MenuItem key={agent.id} value={agent.id}>
								{agent.name}
							</MenuItem>
						))}
				</TextField>
				<TextField
					select
					size="small"
					label="Batch"
					value={batch}
					onChange={(e) => refilter(setBatch)(e.target.value)}
					sx={{ minWidth: 180 }}
				>
					<MenuItem value="">Any</MenuItem>
					{(batchesQuery.data ?? [])
						.filter((b) => teamIds.length === 0 || teamIds.includes(b.teamId))
						.map((b) => (
							<MenuItem key={b.id} value={b.id}>
								{b.name}
							</MenuItem>
						))}
				</TextField>
				<TextField
					select
					size="small"
					label="Direction"
					value={direction}
					onChange={(e) => refilter(setDirection)(e.target.value)}
					sx={{ minWidth: 130 }}
				>
					<MenuItem value="">Any</MenuItem>
					{COSTED_CALL_DIRECTIONS.map((d) => (
						<MenuItem key={d} value={d}>
							{d}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Outcome"
					value={outcome}
					onChange={(e) => refilter(setOutcome)(e.target.value)}
					sx={{ minWidth: 140 }}
				>
					{CALL_OUTCOMES.map((o) => (
						<MenuItem key={o} value={o}>
							{o}
						</MenuItem>
					))}
				</TextField>
			</Stack>
			<Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }} alignItems="center">
				<TextField
					select
					size="small"
					label="Service"
					value={service}
					onChange={(e) => refilter(setService)(e.target.value)}
					sx={{ minWidth: 160 }}
				>
					<MenuItem value="">All services</MenuItem>
					{COST_SERVICES.map((s) => (
						<MenuItem key={s} value={s}>
							{SERVICE_LABELS[s] ?? s}
						</MenuItem>
					))}
				</TextField>
				<TextField
					size="small"
					label="Vendor or gateway"
					value={vendor}
					onChange={(e) => refilter(setVendor)(e.target.value.trim())}
					sx={{ width: 170 }}
				/>
				<TextField
					size="small"
					label="Model"
					value={model}
					onChange={(e) => refilter(setModel)(e.target.value.trim())}
					sx={{ width: 170 }}
				/>
				<TextField
					select
					size="small"
					label="Purpose"
					value={purpose}
					onChange={(e) => refilter(setPurpose)(e.target.value)}
					sx={{ minWidth: 150 }}
				>
					<MenuItem value="">Any</MenuItem>
					{COST_PURPOSES.map((p) => (
						<MenuItem key={p} value={p}>
							{p}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Group by"
					value={groupBy}
					onChange={(e) => setGroupBy(e.target.value)}
					sx={{ minWidth: 140 }}
				>
					{COST_GROUP_BYS.map((g) => (
						<MenuItem key={g} value={g}>
							{g}
						</MenuItem>
					))}
				</TextField>
				<Tooltip title="Re-run with the rate book exactly as it stood at this moment — how a figure somebody already quoted is reproduced after rates changed.">
					<TextField
						size="small"
						type="datetime-local"
						label="Rates as of"
						value={asOf}
						onChange={(e) => setAsOf(e.target.value)}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Tooltip>
				<Button
					variant={drafts.length ? "contained" : "outlined"}
					color={drafts.length ? "warning" : "primary"}
					onClick={() => setDraftOpen(true)}
				>
					What-if{drafts.length ? ` (${drafts.length} draft)` : ""}
				</Button>
				{drafts.length > 0 && <Button onClick={() => setDrafts([])}>Clear drafts</Button>}
			</Stack>
			<Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }} alignItems="center">
				<Tooltip title="What the customer pays per connected minute. Not saved — enter it to see revenue and margin on these calls.">
					<TextField
						size="small"
						label="Selling price / min"
						placeholder="e.g. 3.50"
						value={sellPrice}
						onChange={(e) => setSellPrice(e.target.value.trim())}
						error={sellPrice !== "" && !selling && sellPulse !== ""}
						sx={{ width: 160 }}
					/>
				</Tooltip>
				<Tooltip title="Customer billing pulse in seconds, 1–60. Connected time is rounded up to it; unanswered calls are not billed.">
					<TextField
						size="small"
						label="Customer pulse (s)"
						placeholder="e.g. 15"
						value={sellPulse}
						onChange={(e) => setSellPulse(e.target.value.trim())}
						sx={{ width: 150 }}
					/>
				</Tooltip>
				<Tooltip title="Optional first block, as in 30/15 billing. Blank means the same as the pulse.">
					<TextField
						size="small"
						label="First pulse (s)"
						placeholder="optional"
						value={sellFirst}
						onChange={(e) => setSellFirst(e.target.value.trim())}
						sx={{ width: 130 }}
					/>
				</Tooltip>
				{(sellPrice || sellPulse || sellFirst) && (
					<Button
						onClick={() => {
							setSellPrice("");
							setSellPulse("");
							setSellFirst("");
						}}
					>
						Clear price
					</Button>
				)}
			</Stack>
		</Stack>
	);

	const draftDialog = (
		<RateFormDialog
			open={draftOpen}
			title="Try a rate without saving it"
			submitLabel="Apply to this calculation"
			note="A draft beats a saved rate at the same level, and nothing is written. A saved rate at a MORE specific level still wins — to test a vendor's price where a model-level rate exists, set the model too."
			onClose={() => setDraftOpen(false)}
			onSubmit={(rate) => {
				setDrafts((current) => [...current, rate]);
				setDraftOpen(false);
			}}
		/>
	);

	if (result.error) {
		return (
			<PageShell title="Cost calculator" toolbar={toolbar} maxWidth={false}>
				<Alert severity="error">{result.error.message}</Alert>
				{draftDialog}
			</PageShell>
		);
	}

	const data = result.data;

	if (!data) {
		return (
			<PageShell title="Cost calculator" toolbar={toolbar} maxWidth={false}>
				<LoadingSpinner />
				{draftDialog}
			</PageShell>
		);
	}

	const currency = data.baseCurrency;
	const money = (micros: number | null | undefined) => formatMicros(micros ?? null, currency);
	const pages = Math.max(1, Math.ceil(data.totals.calls / PAGE_SIZE));

	const postCallTotal = data.totals.byPurpose.post_call ?? 0;
	// Post-call traffic that nothing prices — the card must say so rather than
	// present the floor it has as the figure.
	const postCallUnpriced = data.gaps.unpriced.filter(
		(binding) => binding.purpose === "post_call",
	).length;
	const postCallShare =
		data.totals.totalMicros > 0
			? Math.round((postCallTotal / data.totals.totalMicros) * 1000) / 10
			: null;

	return (
		<PageShell
			title="Cost calculator"
			subtitle={`What calls cost in ${currency}, at the rates valid on each call's date (${data.timezone})`}
			toolbar={toolbar}
			maxWidth={false}
		>
			<Stack spacing={3}>
				{data.truncated && (
					<Alert severity="error">
						<AlertTitle>More than {formatCount(data.callCap)} calls match</AlertTitle>
						Every figure here is computed from the newest {formatCount(data.callCap)} and is lower
						than the truth. Narrow the dates, or filter to a workspace, agent or batch.
					</Alert>
				)}

				{(data.asOf !== null || data.draftRatesApplied > 0) && (
					<Alert severity="info">
						{data.asOf !== null && (
							<>Priced with the rate book as it stood at {formatEpoch(data.asOf)}. </>
						)}
						{data.draftRatesApplied > 0 && (
							<>
								{data.draftRatesApplied} unsaved draft rate(s) are applied:{" "}
								{drafts
									.map(
										(d) => `${scopeLabel(d)} ${d.unit} = ${describePricing(d.pricing, d.currency)}`,
									)
									.join("; ")}
								.
							</>
						)}
					</Alert>
				)}

				{data.gaps.unpricedKeys.length > 0 && (
					<Alert severity="warning">
						<AlertTitle>{data.totals.partialCalls} call(s) have legs no rate prices</AlertTitle>
						<Typography variant="body2" sx={{ mb: 1 }}>
							Every total is missing whatever these cost — recorded as unknown, not free. Add a rate
							on the <Link to="/pricing">rate book</Link>.
						</Typography>
						<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
							{data.gaps.unpricedKeys.map((key) => (
								<Chip key={key} label={key} size="small" variant="outlined" />
							))}
						</Stack>
					</Alert>
				)}

				{data.gaps.unconvertedCurrencies.length > 0 && (
					<Alert severity="warning">
						Lines priced in {data.gaps.unconvertedCurrencies.join(", ")} have no conversion rate for
						their day, so they are left out of every total. Add one on the{" "}
						<Link to="/pricing?tab=fx">rate book</Link>.
					</Alert>
				)}

				{(data.gaps.inheritedKeys.length > 0 || data.gaps.costedFromTotalKeys.length > 0) && (
					<Alert severity="info">
						{data.gaps.inheritedKeys.length > 0 && (
							<Typography variant="body2">
								Priced by a service- or gateway-level default rather than their own rate:{" "}
								{data.gaps.inheritedKeys.join(", ")}. Right if that default is meant to cover them;
								a guess if it is not.
							</Typography>
						)}
						{data.gaps.costedFromTotalKeys.length > 0 && (
							<Typography variant="body2">
								A per-event rate was applied to the total because the events behind it were not all
								recorded: {data.gaps.costedFromTotalKeys.join(", ")}.
							</Typography>
						)}
					</Alert>
				)}

				<Grid container spacing={2}>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<Stat
							label={`Total (${currency})`}
							value={money(data.totals.totalMicros)}
							hint={
								data.totals.partialCalls > 0
									? `a floor — ${data.totals.partialCalls} call(s) have an unpriced leg`
									: "every counted line priced"
							}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<Stat
							label="Cost per connected minute"
							value={money(data.totals.costPerConnectedMinuteMicros)}
							hint={`all spend ÷ ${minutes(data.totals.connectedSeconds)} connected`}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<Stat
							label="Calls"
							value={formatCount(data.totals.calls)}
							hint={`${formatCount(data.totals.connectedCalls)} connected, ${formatCount(data.totals.unansweredCalls)} unanswered`}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<Stat
							label="Average per call"
							value={money(data.totals.avgPerCallMicros)}
							hint={`${money(data.totals.avgPerConnectedCallMicros)} per connected call`}
						/>
					</Grid>
				</Grid>

				{data.selling && (
					<Card variant="outlined" sx={{ p: 2 }}>
						<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
							At {money(data.selling.pricePerMinuteMicros)}/min,{" "}
							{data.selling.firstPulseSeconds === data.selling.pulseSeconds
								? `${data.selling.pulseSeconds}s pulse`
								: `${data.selling.firstPulseSeconds}/${data.selling.pulseSeconds}s pulse`}
						</Typography>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Stat
									label="Revenue"
									value={money(data.selling.revenueMicros)}
									hint={`${minutes(data.selling.billedSeconds)} billed for ${minutes(data.totals.connectedSeconds)} connected`}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Stat
									label="Margin"
									value={money(data.selling.marginMicros)}
									hint={`${percent(data.selling.marginBps)} of revenue — calls only, before overheads`}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Stat
									label="Earned per connected minute"
									value={money(data.selling.revenuePerConnectedMinuteMicros)}
									hint={`costs ${money(data.totals.costPerConnectedMinuteMicros)}`}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<Stat
									label="Cost of these calls"
									value={money(data.selling.costMicros)}
									hint={`unanswered attempts ${money(data.totals.unansweredCostMicros)} of it, and earn nothing`}
								/>
							</Grid>
						</Grid>
					</Card>
				)}

				<Card variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2">
						Which customer pulse — measured on these calls
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
						The carrier bills us in 60s pulses; the customer is billed in the pulse below. A shorter
						pulse bills fewer seconds, so it needs a higher price per minute to cover the same cost.
						Break-even is the price per minute at which revenue exactly covers these calls' cost.
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Customer pulse</TableCell>
								<TableCell align="right">Billed time</TableCell>
								<TableCell align="right">Billed ÷ connected</TableCell>
								<TableCell align="right">Break-even / min</TableCell>
								{data.selling && <TableCell align="right">Revenue at your price</TableCell>}
								{data.selling && <TableCell align="right">Margin</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{data.pulseOptions.map((option) => (
								<TableRow
									key={option.pulseSeconds}
									selected={data.selling?.pulseSeconds === option.pulseSeconds}
								>
									<TableCell>{option.pulseSeconds}s</TableCell>
									<TableCell align="right">{minutes(option.billedSeconds)}</TableCell>
									<TableCell align="right">
										{data.totals.connectedSeconds > 0
											? `${(option.billedSeconds / data.totals.connectedSeconds).toFixed(2)}×`
											: "—"}
									</TableCell>
									<TableCell align="right">{money(option.breakEvenPricePerMinuteMicros)}</TableCell>
									{data.selling && (
										<TableCell align="right">{money(option.revenueMicros)}</TableCell>
									)}
									{data.selling && (
										<TableCell
											align="right"
											sx={{
												color: (option.marginMicros ?? 0) < 0 ? "error.main" : "success.main",
											}}
										>
											{money(option.marginMicros)}
										</TableCell>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Card>

				<Card variant="outlined" sx={{ p: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1.5 }}>
						By service
					</Typography>
					<Grid container spacing={2}>
						{COST_SERVICES.map((s) => (
							<Grid key={s} size={{ xs: 6, md: 12 / 5 }}>
								<Stat label={SERVICE_LABELS[s] ?? s} value={money(data.totals.byService[s] ?? 0)} />
							</Grid>
						))}
					</Grid>
					<Box sx={{ mt: 2 }}>
						<Stat
							label="of which post-call analysis (summary + extraction)"
							value={money(postCallTotal)}
							hint={
								postCallUnpriced > 0
									? `a floor — ${postCallUnpriced} post-call binding(s) have no rate; see the rate book's post-call panel`
									: postCallShare === null
										? "included in Inference above"
										: `${postCallShare}% of the total, included in Inference above`
							}
						/>
					</Box>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
						Unanswered attempts cost {money(data.totals.unansweredCostMicros)} of the total.{" "}
						{data.totals.sessionClockCalls > 0 &&
							`${data.totals.sessionClockCalls} call(s) had no carrier leg (browser), so their connected minutes are the session's own clock.`}{" "}
						{data.totals.carrierClockCalls > 0 &&
							`${data.totals.carrierClockCalls} call(s) had no duration from the carrier, so their connected minutes are its answer→hangup timestamps.`}
					</Typography>
				</Card>

				<Card variant="outlined" sx={{ p: 2 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="baseline">
						<Typography variant="subtitle2">
							Overheads — platform-wide, not narrowed by the filters
						</Typography>
						<Typography variant="subtitle2">{money(data.overheads.totalMicros)}</Typography>
					</Stack>
					{data.overheads.lines.length === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							No fixed fees or volume slabs apply to these dates. They are entered on the{" "}
							<Link to="/pricing?tab=fees">rate book</Link>, and are never allocated into a call.
						</Typography>
					) : (
						<Table size="small" sx={{ mt: 1 }}>
							<TableHead>
								<TableRow>
									<TableCell>Month</TableCell>
									<TableCell>What</TableCell>
									<TableCell align="right">Share of month</TableCell>
									<TableCell align="right">Quantity</TableCell>
									<TableCell align="right">Amount</TableCell>
									<TableCell>Note</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.overheads.lines.map((line) => (
									<TableRow key={`${line.id}:${line.month}`}>
										<TableCell>{line.month}</TableCell>
										<TableCell>
											{line.kind === "fixed_fee" ? "Fixed fee: " : "Slab adjustment: "}
											{line.label}
										</TableCell>
										<TableCell align="right">{Math.round(line.share * 100)}%</TableCell>
										<TableCell align="right">
											{formatCount(line.quantity === null ? null : Math.round(line.quantity))}
										</TableCell>
										<TableCell align="right">{money(line.amountMicros)}</TableCell>
										<TableCell>
											<Typography variant="caption" color="text.secondary">
												{line.note}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Card>

				{data.groups.length > 0 && (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Grouped by {groupBy}
						</Typography>
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>{groupBy}</TableCell>
										<TableCell align="right">Calls</TableCell>
										<TableCell align="right">Connected</TableCell>
										<TableCell align="right">Connected minutes</TableCell>
										<TableCell align="right">Total</TableCell>
										<TableCell align="right">Per call</TableCell>
										<TableCell align="right">Per connected minute</TableCell>
										{data.selling && <TableCell align="right">Revenue</TableCell>}
										{data.selling && <TableCell align="right">Margin</TableCell>}
									</TableRow>
								</TableHead>
								<TableBody>
									{data.groups.map((group) => (
										<TableRow key={group.key}>
											<TableCell>{group.label}</TableCell>
											<TableCell align="right">{formatCount(group.calls)}</TableCell>
											<TableCell align="right">{formatCount(group.connectedCalls)}</TableCell>
											<TableCell align="right">{minutes(group.connectedSeconds)}</TableCell>
											<TableCell align="right">{money(group.totalMicros)}</TableCell>
											<TableCell align="right">{money(group.avgPerCallMicros)}</TableCell>
											<TableCell align="right">
												{money(group.costPerConnectedMinuteMicros)}
											</TableCell>
											{data.selling && (
												<TableCell align="right">{money(group.revenueMicros)}</TableCell>
											)}
											{data.selling && (
												<TableCell
													align="right"
													sx={{
														color: (group.marginMicros ?? 0) < 0 ? "error.main" : undefined,
													}}
												>
													{money(group.marginMicros)}
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						By binding — which rate priced what
					</Typography>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Service</TableCell>
									<TableCell>Binding</TableCell>
									<TableCell>Unit</TableCell>
									<TableCell align="right">Quantity</TableCell>
									<TableCell align="right">Calls</TableCell>
									<TableCell>Rate level</TableCell>
									<TableCell align="right">Amount</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.breakdown.map((row) => (
									<TableRow key={row.key}>
										<TableCell>{SERVICE_LABELS[row.service] ?? row.service}</TableCell>
										<TableCell>
											{[row.gateway, row.vendor, row.model || "(default)"]
												.filter(Boolean)
												.join(" › ")}
											{row.purpose !== "conversation" && (
												<Chip label={row.purpose} size="small" sx={{ ml: 1 }} />
											)}
										</TableCell>
										<TableCell>{row.unit}</TableCell>
										<TableCell align="right">{formatCount(Math.round(row.quantity))}</TableCell>
										<TableCell align="right">{formatCount(row.calls)}</TableCell>
										<TableCell>{row.levels.join(", ") || "—"}</TableCell>
										<TableCell align="right">
											{/* Nothing under this binding was priced: a dash, not ₹0.00. */}
											{row.levels.length === 0 ? "—" : money(row.amountMicros)}
											{row.unpricedLines + row.unconvertedLines > 0 && (
												<Chip label="partial" size="small" color="warning" sx={{ ml: 1 }} />
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>

				{data.reconciliation.calls > 0 && (
					<Alert
						severity={
							Math.abs(data.reconciliation.ourTelephonyMicros - data.reconciliation.carrierMicros) >
							data.reconciliation.carrierMicros * 0.02
								? "warning"
								: "success"
						}
					>
						Telephony on the {formatCount(data.reconciliation.calls)} call(s) where the carrier
						reported its own charge: ours {money(data.reconciliation.ourTelephonyMicros)}, the
						carrier's {money(data.reconciliation.carrierMicros)}. A gap means the entered rate or
						pulse does not match what the carrier bills.
					</Alert>
				)}

				<Box>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
						<Typography variant="subtitle2">
							Per call — newest first ({formatCount(data.totals.calls)} total)
						</Typography>
						<Button
							size="small"
							variant="outlined"
							onClick={() =>
								download(`costs_${from}_${to}_p${page}.csv`, callsToCsv(data.calls, currency))
							}
						>
							Export this page as CSV
						</Button>
					</Stack>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Started</TableCell>
									<TableCell>Workspace</TableCell>
									<TableCell>Agent</TableCell>
									<TableCell>Direction</TableCell>
									<TableCell align="right">Connected</TableCell>
									{CSV_SERVICES.map((s) => (
										<TableCell key={s} align="right">
											{SERVICE_LABELS[s]}
										</TableCell>
									))}
									<TableCell align="right">
										<Tooltip title="Summary and extraction after hangup. Already inside Inference — not added to the total again.">
											<span>Post-call (of Inference)</span>
										</Tooltip>
									</TableCell>
									<TableCell align="right">Total</TableCell>
									<TableCell align="right">Per connected min</TableCell>
									{data.selling && <TableCell align="right">Revenue</TableCell>}
								</TableRow>
							</TableHead>
							<TableBody>
								{data.calls.map((call) => (
									<Fragment key={call.sessionId}>
										<TableRow
											hover
											onClick={() => setOpenCall(openCall === call.sessionId ? "" : call.sessionId)}
											sx={{ cursor: "pointer" }}
										>
											<TableCell sx={{ whiteSpace: "nowrap" }}>
												{formatEpoch(call.startedAt)}
											</TableCell>
											<TableCell>{call.teamName ?? "—"}</TableCell>
											<TableCell>{call.agentName ?? "—"}</TableCell>
											<TableCell>{call.direction}</TableCell>
											<TableCell align="right">
												<Tooltip title={CONNECTED_SOURCE_LABEL[call.connectedSource]}>
													<span>
														{call.connected ? minutes(call.connectedSeconds) : "unanswered"}
													</span>
												</Tooltip>
											</TableCell>
											{CSV_SERVICES.map((s) => (
												<TableCell key={s} align="right">
													{call.byService[s] === undefined ? "—" : money(call.byService[s])}
												</TableCell>
											))}
											<TableCell align="right">
												{postCallMicros(call) === undefined ? "—" : money(postCallMicros(call))}
											</TableCell>
											<TableCell align="right">
												{call.partial && (
													<Chip label="partial" size="small" color="warning" sx={{ mr: 1 }} />
												)}
												{money(call.totalMicros)}
											</TableCell>
											<TableCell align="right">
												{money(call.costPerConnectedMinuteMicros)}
											</TableCell>
											{data.selling && (
												<TableCell align="right">{money(call.revenueMicros)}</TableCell>
											)}
										</TableRow>
										{openCall === call.sessionId && (
											<TableRow>
												<TableCell colSpan={8 + CSV_SERVICES.length + (data.selling ? 1 : 0)}>
													<CallLines
														call={call}
														currency={currency}
														onOpen={() => navigate(`/sessions/${call.sessionId}`)}
													/>
												</TableCell>
											</TableRow>
										)}
									</Fragment>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{pages > 1 && (
						<Stack alignItems="center" sx={{ mt: 1.5 }}>
							<Pagination count={pages} page={page} onChange={(_event, next) => setPage(next)} />
						</Stack>
					)}
				</Box>
			</Stack>
			{draftDialog}
		</PageShell>
	);
};

/** Every line behind one call: the quantity, the rate level that priced it, and why a line has no amount. */
function CallLines({
	call,
	currency,
	onOpen,
}: {
	call: CallCost;
	currency: string;
	onOpen: () => void;
}) {
	return (
		<Stack spacing={1}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Service</TableCell>
						<TableCell>Binding</TableCell>
						<TableCell>Unit</TableCell>
						<TableCell>Source</TableCell>
						<TableCell align="right">Quantity</TableCell>
						<TableCell align="right">Events</TableCell>
						<TableCell>Rate</TableCell>
						<TableCell align="right">In rate currency</TableCell>
						<TableCell align="right">{currency}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{call.lines.map((line, index) => (
						<TableRow
							key={`${line.service}:${line.vendor}:${line.model}:${line.unit}:${line.source}:${line.purpose}:${index}`}
						>
							<TableCell>{line.service}</TableCell>
							<TableCell>
								{[line.gateway, line.vendor, line.model || "(default)"].filter(Boolean).join(" › ")}
								{line.purpose !== "conversation" && ` [${line.purpose}]`}
							</TableCell>
							<TableCell>{line.unit}</TableCell>
							<TableCell>{line.source}</TableCell>
							<TableCell align="right">
								{new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(
									line.quantity,
								)}
							</TableCell>
							<TableCell align="right">{line.events ?? "—"}</TableCell>
							<TableCell>
								{line.source === "vendor"
									? "vendor's own figure — compared, not charged"
									: line.gap === "unpriced"
										? "no rate"
										: `${line.level}${line.costedFromTotal ? ", from total" : ""}`}
							</TableCell>
							<TableCell align="right">
								{line.rateAmountMicros === null
									? "—"
									: formatMicros(line.rateAmountMicros, line.currency ?? currency)}
							</TableCell>
							<TableCell align="right">
								{line.gap === "unconverted"
									? `no ${line.currency} rate`
									: formatMicros(line.amountMicros, currency)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
			<Stack direction="row" spacing={2} alignItems="center">
				{call.carrierCostMicros !== null && (
					<Typography variant="caption" color="text.secondary">
						Carrier reported {formatMicros(call.carrierCostMicros, currency)} for this call.
					</Typography>
				)}
				<Button size="small" onClick={onOpen}>
					Open session
				</Button>
			</Stack>
		</Stack>
	);
}
